import { useEffect, useCallback, useRef } from 'react'

interface UseInfiniteScrollOptions {
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => void
  threshold?: number // Distance from bottom to trigger load (in pixels)
  debounceMs?: number // Debounce delay for scroll events
}

export const useInfiniteScroll = (
  containerRef: React.RefObject<HTMLElement>,
  options: UseInfiniteScrollOptions
) => {
  const { hasNextPage, isFetchingNextPage, fetchNextPage, threshold = 100, debounceMs = 100 } = options
  const debounceTimer = useRef<NodeJS.Timeout | undefined>(undefined)

  const handleScroll = useCallback(() => {
    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    // Set new timer
    debounceTimer.current = setTimeout(() => {
      const container = containerRef.current
      if (!container || !hasNextPage || isFetchingNextPage) return

      const { scrollTop, scrollHeight, clientHeight } = container
      
      // Trigger when user scrolls to within threshold pixels of the bottom
      if (scrollTop + clientHeight >= scrollHeight - threshold) {
        console.log('Infinite scroll triggered, loading more content...')
        fetchNextPage()
      }
    }, debounceMs)
  }, [containerRef, hasNextPage, isFetchingNextPage, fetchNextPage, threshold, debounceMs])

  useEffect(() => {
    const container = containerRef.current
    if (!container) {
      return undefined
    }

    container.addEventListener('scroll', handleScroll, { passive: true })
    
    return () => {
      container.removeEventListener('scroll', handleScroll)
      // Clean up any pending timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [handleScroll, containerRef])
}
